/**
 * The bootstrap step that gives the application its API address (TASK-0076).
 *
 * This module runs **before** React: `main.tsx` awaits {@link configureRuntime} and only then mounts the application.
 * That ordering is the whole point — `App` starts the silent session restore on mount, which posts to
 * `<apiBaseUrl>/auth/refresh`, so a configuration resolved one tick later would send the first request of every page load
 * to the wrong origin.
 *
 * Two outcomes:
 *
 * - a usable configuration is applied to `env` and the application starts;
 * - a Production deployment without one gets {@link ConfigurationError}, and the caller renders
 *   {@link renderStartupError} instead of the application — a screen that needs no configuration to render.
 *
 * A Development build never fails here: a missing file is a warning and the `.env` fallback applies, which keeps
 * `npm run dev` and `npm run preview` working as they did before this change.
 */

import { appEnvironment, applyRuntimeConfig, runtimeConfigUrl } from '@/lib/env';
import {
  loadRuntimeConfig,
  resolveRuntimeConfig,
  type ConfigFetch,
  type ConfigurationProblem,
  type LocationLike,
  type RuntimeConfig,
  type RuntimeConfigInput,
} from '@/lib/runtimeConfig';

/** What a caller needs to know when the application must not start. */
export class ConfigurationError extends Error {
  /** Every problem found, so the screen lists them instead of sending the operator round once per mistake. */
  readonly problems: ConfigurationProblem[];

  /** Where the file was looked for, so the message can name it. */
  readonly configUrl: string;

  /**
   * @param problems The problems found while resolving the configuration.
   * @param configUrl URL the runtime document was read from.
   */
  constructor(problems: ConfigurationProblem[], configUrl: string) {
    super(`The application cannot start: its configuration has ${problems.length} problem(s).`);
    this.name = 'ConfigurationError';
    this.problems = problems;
    this.configUrl = configUrl;
  }
}

/** Options that let a test drive {@link configureRuntime} without a network or a DOM. */
export interface ConfigureRuntimeOptions {
  /** Transport for the runtime file; defaults to the global `fetch`. */
  fetchImpl?: ConfigFetch;
  /** Page the application runs on; defaults to `window.location`. */
  location?: LocationLike;
  /** Environment name; defaults to {@link appEnvironment}. */
  environment?: string;
  /** URL of the runtime file; defaults to {@link runtimeConfigUrl}. */
  configUrl?: string;
  /** Abort delay for the file; see `CONFIG_FETCH_TIMEOUT_MS`. */
  timeoutMs?: number;
  /** Called with any problem that did not prevent start-up, so it can reach the console. */
  onWarning?: (problems: ConfigurationProblem[]) => void;
}

/** Copies the page's location into the shape the resolver takes. */
function currentLocation(): LocationLike {
  return {
    search: window.location.search,
    hash: window.location.hash,
    origin: window.location.origin,
    protocol: window.location.protocol,
  };
}

/**
 * The build-time half of the resolver's input.
 *
 * `import.meta.env` is read here rather than in `runtimeConfig`, so the resolver stays a pure function whose every input a
 * test can state explicitly.
 */
function buildTimeInput(configUrl: string, environment: string): RuntimeConfigInput['build'] {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    apiTimeoutMs: import.meta.env.VITE_API_TIMEOUT_MS,
    tokenRefreshSkewSeconds: import.meta.env.VITE_TOKEN_REFRESH_SKEW_SECONDS,
    configUrl,
    runtimeConfigUrl: import.meta.env.VITE_RUNTIME_CONFIG_URL,
    environment,
  };
}

/**
 * Loads, validates and applies the runtime configuration.
 *
 * @param options Test seams; production calls it with none.
 * @returns The configuration that is now in effect.
 * @throws {ConfigurationError} When the deployment's configuration cannot be used.
 */
export async function configureRuntime(
  options: ConfigureRuntimeOptions = {},
): Promise<RuntimeConfig> {
  const configUrl = options.configUrl ?? runtimeConfigUrl();
  const environment = options.environment ?? appEnvironment();
  const location = options.location ?? currentLocation();
  const build = buildTimeInput(configUrl, environment);

  // A URL override is enough on its own: fetching the file would be a wasted round trip only to be overridden, and on a
  // host where the file is the broken thing this is the route that still starts the application.
  const overrideOnly = resolveRuntimeConfig({
    build,
    environment,
    location,
    configText: undefined,
    file: { status: 'absent' },
  });

  const file =
    overrideOnly.ok && overrideOnly.config.source === 'url-override'
      ? ({ status: 'absent' } as const)
      : await loadRuntimeConfig({
          configUrl,
          fetchImpl: options.fetchImpl,
          timeoutMs: options.timeoutMs,
        });

  const result = resolveRuntimeConfig({
    build,
    environment,
    location,
    configText: undefined,
    file,
  });

  if (!result.ok) {
    throw new ConfigurationError(result.problems, configUrl);
  }

  applyRuntimeConfig(result.config);

  if (result.warnings.length > 0) {
    options.onWarning?.(result.warnings);
  }

  return result.config;
}
